import * as React from 'react';
import {
  AlertBaseAction,
  type AlertBaseActionProps,
} from 'src/components/AlertBaseAction/AlertBaseAction';
import { capitalize, styled } from '@mui/material';
import {
  type IAlertBaseContext,
  useAlertBaseSeverity,
} from 'src/components/AlertBase/AlertBaseContext';
import { getAlertBaseUtilityClass } from 'src/components/AlertBase/alertBaseClasses';
import { getToken } from '@workiva/unify-tokens';
import { globalMuiClasses } from 'src/utils/cssSelectorConstants';
import { getAlertBaseStyledOverridesResolver } from 'src/components/AlertBase/styledAlertBaseOverridesResolver';

export type AlertActionProps = AlertBaseActionProps;

type UnifyAlertActionOwnerState = Required<Pick<IAlertBaseContext, 'severity'>>;

const UnifyAlertActionRoot = styled(AlertBaseAction, {
  name: 'UnifyAlertAction',
  slot: 'Root',
  overridesResolver: getAlertBaseStyledOverridesResolver('child'),
})<AlertBaseActionProps & { ownerState: UnifyAlertActionOwnerState }>(
  ({ ownerState }) => {
    const { severity } = ownerState;
    const severityToken = severity === 'info' ? 'primary' : severity;

    return {
      [`.${getAlertBaseUtilityClass(`severity${capitalize(severity)}`)} &`]: {
        color: getToken(
          `Component/Alert/alert-${severityToken}-action-onColor`
        ),

        [`&:hover, &.${globalMuiClasses.focusVisible}`]: {
          color: getToken(
            `Component/Alert/alert-${severityToken}-action-onColor-hover`
          ),
        },
      },
    };
  }
);

/**
 * Use the `AlertAction` component within the `actions` prop of an `Alert` to display up to two actions.
 */
export const AlertAction = React.forwardRef<
  HTMLButtonElement,
  AlertActionProps
>(function AlertAction(props, ref) {
  const severity = useAlertBaseSeverity();
  /* eslint-disable @typescript-eslint/no-unused-vars,deprecation/deprecation */
  return (
    <UnifyAlertActionRoot {...props} ref={ref} ownerState={{ severity }} />
  );
});

export default AlertAction;
